import { atom } from "jotai";
import { SelectOption } from "@ir/ui";
import { FilterDateRange } from "@/presentation/deduction/components/DeductionFilter/interfaces";
import {
  IDeductionsByPurchaserItem,
  IDeductionsByTypeAndReasonGroup,
  IDeductionVolumeVsGrossSalesItem,
  IDeductionsByAgingBracketsRepositoryResponse,
  IDisputePipelineRepositoryResponse,
  IDeductionsByRetailerRepositoryResponse,
  ScoreCardTrend,
} from "@/core/repositories/analytics";
export type {
  ScoreCardTrend,
  AnalyticsDeductionDimension,
  IDeductionsByTypeAndReasonRow,
  IDeductionsByTypeAndReasonGroup,
} from "@/core/repositories/analytics";
export type { IDeductionsByPurchaserItem } from "@/core/repositories/analytics";

export interface AnalyticsFilterInfo {
  checkFromDate: string | null;
  checkToDate: string | null;
  checkDateRange: FilterDateRange | null;
  invoiceFromDate: string | null;
  invoiceToDate: string | null;
  invoiceDateRange: FilterDateRange | null;
  purchaser: SelectOption[];
}

export interface AnalyticsState {
  filterInfo: AnalyticsFilterInfo;
  isFilterApplied: boolean;
  isHydrated: boolean;
}

const initialFilterInfo: AnalyticsFilterInfo = {
  checkFromDate: null,
  checkToDate: null,
  checkDateRange: null,
  invoiceFromDate: null,
  invoiceToDate: null,
  invoiceDateRange: null,
  purchaser: [],
};

const initialState: AnalyticsState = {
  filterInfo: initialFilterInfo,
  isFilterApplied: false,
  isHydrated: false,
};

export const analyticsAtom = atom<AnalyticsState>(initialState);

export const analyticsFilterInfoAtom = atom(get => get(analyticsAtom).filterInfo);
export const isAnalyticsFilterAppliedAtom = atom(get => get(analyticsAtom).isFilterApplied);
export const isAnalyticsHydratedAtom = atom(get => get(analyticsAtom).isHydrated);

// --- Score Cards ---

export type ScoreCardTheme = "warning" | "error" | "secondary" | "success";

export interface ScoreCardData {
  primaryValue: string | null;
  secondaryValue: string | null;
  chipLabel: string | null;
  footerText: string | null;
  trend: ScoreCardTrend | null;
  tooltipMarkdown: string | null;
  isLoading: boolean;
  error: Error | null;
}

export interface AnalyticsScoreCardsState {
  unresolvedDeductions: ScoreCardData;
  raisedForDispute: ScoreCardData;
  deductionsGrossSales: ScoreCardData;
  tradeLinkedDeductions: ScoreCardData;
}

const initialScoreCardData: ScoreCardData = {
  primaryValue: null,
  secondaryValue: null,
  chipLabel: null,
  footerText: null,
  trend: null,
  tooltipMarkdown: null,
  isLoading: false,
  error: null,
};

const initialScoreCardsState: AnalyticsScoreCardsState = {
  unresolvedDeductions: { ...initialScoreCardData },
  raisedForDispute: { ...initialScoreCardData },
  deductionsGrossSales: { ...initialScoreCardData },
  tradeLinkedDeductions: { ...initialScoreCardData },
};

export const analyticsScoreCardsAtom = atom<AnalyticsScoreCardsState>(initialScoreCardsState);

export const unresolvedDeductionsCardAtom = atom(get => get(analyticsScoreCardsAtom).unresolvedDeductions);
export const raisedForDisputeCardAtom = atom(get => get(analyticsScoreCardsAtom).raisedForDispute);
export const deductionsGrossSalesCardAtom = atom(get => get(analyticsScoreCardsAtom).deductionsGrossSales);
export const tradeLinkedDeductionsCardAtom = atom(get => get(analyticsScoreCardsAtom).tradeLinkedDeductions);

// --- Deductions By Purchaser KPI ---

export interface DeductionsByPurchaserState {
  items: IDeductionsByPurchaserItem[];
  isLoading: boolean;
  error: Error | null;
}

const initialDeductionsByPurchaserState: DeductionsByPurchaserState = {
  items: [],
  isLoading: false,
  error: null,
};

export const analyticsDeductionsByPurchaserAtom = atom<DeductionsByPurchaserState>(initialDeductionsByPurchaserState);
export const deductionsByPurchaserAtom = atom(get => get(analyticsDeductionsByPurchaserAtom));

// --- Deductions By Type / Reason KPI ---

export interface DeductionsByTypeAndReasonState {
  groups: IDeductionsByTypeAndReasonGroup[];
  isLoading: boolean;
  error: Error | null;
}

const initialDeductionsByTypeAndReasonState: DeductionsByTypeAndReasonState = {
  groups: [],
  isLoading: false,
  error: null,
};

export const analyticsDeductionsByTypeAndReasonAtom = atom<DeductionsByTypeAndReasonState>(
  initialDeductionsByTypeAndReasonState
);
export const deductionsByTypeAndReasonAtom = atom(get => get(analyticsDeductionsByTypeAndReasonAtom));

// --- Deduction Volume vs Gross Sales KPI ---

export interface DeductionVolumeVsGrossSalesState {
  items: IDeductionVolumeVsGrossSalesItem[];
  isLoading: boolean;
  error: Error | null;
}

const initialDeductionVolumeVsGrossSalesState: DeductionVolumeVsGrossSalesState = {
  items: [],
  isLoading: false,
  error: null,
};

export const analyticsDeductionVolumeVsGrossSalesAtom = atom<DeductionVolumeVsGrossSalesState>(
  initialDeductionVolumeVsGrossSalesState
);
export const deductionVolumeVsGrossSalesAtom = atom(get => get(analyticsDeductionVolumeVsGrossSalesAtom));

// --- Deductions By Aging Brackets KPI ---

export interface DeductionsByAgingBracketsState {
  data: IDeductionsByAgingBracketsRepositoryResponse | null;
  isLoading: boolean;
  error: Error | null;
}

const initialDeductionsByAgingBracketsState: DeductionsByAgingBracketsState = {
  data: null,
  isLoading: false,
  error: null,
};

export const analyticsDeductionsByAgingBracketsAtom = atom<DeductionsByAgingBracketsState>(
  initialDeductionsByAgingBracketsState
);
export const deductionsByAgingBracketsAtom = atom(get => get(analyticsDeductionsByAgingBracketsAtom));

// --- Dispute Pipeline KPI ---

export interface DisputePipelineState {
  data: IDisputePipelineRepositoryResponse | null;
  isLoading: boolean;
  error: Error | null;
}

const initialDisputePipelineState: DisputePipelineState = {
  data: null,
  isLoading: false,
  error: null,
};

export const analyticsDisputePipelineAtom = atom<DisputePipelineState>(initialDisputePipelineState);
export const disputePipelineAtom = atom(get => get(analyticsDisputePipelineAtom));

// --- Deductions By Retailer KPI ---

export interface DeductionsByRetailerState {
  data: IDeductionsByRetailerRepositoryResponse | null;
  isLoading: boolean;
  error: Error | null;
}

const initialDeductionsByRetailerState: DeductionsByRetailerState = {
  data: null,
  isLoading: false,
  error: null,
};

export const analyticsDeductionsByRetailerAtom = atom<DeductionsByRetailerState>(initialDeductionsByRetailerState);
export const deductionsByRetailerAtom = atom(get => get(analyticsDeductionsByRetailerAtom));
